/**
 * Invoice Calculation Utilities
 * Fatura hesaplama fonksiyonları - Saf fonksiyonlar (Pure Functions)
 */

import { InvoiceItem, TevkifatType } from '../components/types/invoice.types';

/**
 * Tevkifat Türleri Listesi
 */
export const tevkifatTypes: TevkifatType[] = [
  { code: "601", description: "Yapım İşleri ile Bu İşlerle Birlikte İfa Edilen Mühendislik-Mimarlık ve Etüt-Proje Hizmetleri", rate: "4/10" },
  { code: "602", description: "Etüt, Plan-Proje, Danışmanlık, Denetim ve Benzeri Hizmetler", rate: "9/10" },
  { code: "603", description: "Makine, Teçhizat, Demirbaş ve Taşıtlara Ait Tadil, Bakım ve Onarım Hizmetleri", rate: "7/10" },
  { code: "604", description: "Yemek Servis Hizmeti", rate: "5/10" },
  { code: "605", description: "Organizasyon Hizmeti", rate: "5/10" },
  { code: "606", description: "İşgücü Temin Hizmetleri", rate: "9/10" },
  { code: "607", description: "Özel Güvenlik Hizmeti", rate: "9/10" },
  { code: "608", description: "Yapı Denetim Hizmetleri", rate: "9/10" },
  { code: "609", description: "Fason Olarak Yaptırılan Tekstil ve Konfeksiyon İşleri, Çanta ve Ayakkabı Dikim İşleri ve Bu İşlere Aracılık Hizmetleri", rate: "7/10" },
  { code: "610", description: "Turistik Mağazalara Verilen Müşteri Bulma / Götürme Hizmetleri", rate: "9/10" },
  { code: "611", description: "Spor Kulüplerinin Yayın, Reklâm ve İsim Hakkı Gelirlerine Konu İşlemleri", rate: "9/10" },
  { code: "612", description: "Temizlik Hizmeti", rate: "9/10" },
  { code: "613", description: "Çevre ve Bahçe Bakım Hizmetleri", rate: "9/10" },
  { code: "614", description: "Servis Taşımacılığı Hizmeti", rate: "5/10" },
  { code: "615", description: "Her Türlü Baskı ve Basım Hizmetleri", rate: "7/10" },
  { code: "616", description: "5018 sayılı kanuna ekli cetvellerdeki idare, kurum ve kuruşlara yapılan diğer hizmetler", rate: "5/10" },
  { code: "617", description: "Hurda metalden elde edilen külçe teslimleri", rate: "7/10" },
  { code: "618", description: "Hurda metalden elde edilenler dışındaki bakır, çinko ve alüminyum külçe teslimleri", rate: "7/10" },
  { code: "619", description: "Bakır, çinko ve alüminyum ürünlerinin teslimi", rate: "7/10" },
  { code: "620", description: "İstisnadan vazgeçenlerin hurda ve atık teslimi", rate: "7/10" },
  { code: "621", description: "Metal, plastik, lastik, kauçuk, kâğıt ve cam hurda ve atıklardan elde edilen hammadde teslimi", rate: "9/10" },
  { code: "622", description: "Pamuk, tiftik, yün ve yapağı ile ham post ve deri teslimleri", rate: "9/10" },
  { code: "623", description: "Ağaç ve orman ürünleri teslimi", rate: "5/10" },
  { code: "624", description: "Yük taşımacılığı hizmeti", rate: "2/10" },
  { code: "625", description: "Ticari reklam hizmetleri", rate: "3/10" },
  { code: "626", description: "Diğer teslimler", rate: "2/10" },
  { code: "627", description: "Demir-Çelik ürünlerinin teslimi", rate: "5/10" }
];

/**
 * Miktarı string'den number'a çevirir (virgül desteği ile)
 */
export const parseQuantityString = (val: string | undefined | null): number => {
  if (!val) return 0;
  const cleaned = val.toString().replace(',', '.');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Sayıyı görüntüleme formatına çevirir (virgül ile)
 */
export const formatQuantityForDisplay = (num: number): string => {
  return num.toString().replace('.', ',');
};

/**
 * Yuvarlama fonksiyonları - GİB e-fatura standardına uygun
 * 
 * KULLANIM:
 * - roundTo4(): Ara hesaplamalar (birim fiyat, oranlar) - Hassasiyet için
 * - roundTo2(): Fatura tutarları (satır, toplam, KDV) - Kuruş seviyesi (faturalanabilir)
 */

/**
 * 4 ondalık yuvarlama - Ara hesaplamalar için
 * Örnek: 41.666666... → 41.6667
 */
export const roundTo4 = (num: number): number => {
  return Math.round(num * 10000) / 10000;
};

/**
 * 2 ondalık yuvarlama - Fatura tutarları için (kuruş hassasiyeti)
 * Örnek: 41.6667 → 41.67 TL
 */
export const roundTo2 = (num: number): number => {
  return Math.round(num * 100) / 100;
};

/**
 * Tek bir fatura kalemini yeniden hesaplar
 * ✅ HİBRİT HASSASIYET: Ara hesap 4 ondalık, fatura tutarları 2 ondalık
 * 
 * MANTIK:
 * 1. Birim fiyat: 4 ondalık (hassasiyet kaybını önler)
 * 2. Satır tutarları: 2 ondalık (faturalanabilir tutar - kuruş seviyesi)
 * 3. KDV/ÖTV: 2 ondalık (faturalanabilir tutar)
 * 4. Toplam: 2 ondalık (faturalanabilir tutar)
 */
export const recalculateLineItem = (item: InvoiceItem): InvoiceItem => {
  const qty = item.quantity;
  const enteredPrice = item.enteredPrice;
  const vatRate = item.vatRate;
  const sctRate = item.sctRate;
  const discountRate = item.discountRate;
  const priceType = item.priceType;

  let unitPrice: number;
  let unitPriceWithVat: number;

  // ═══════════════════════════════════════════════════════
  // ADIM 1: BİRİM FİYAT HESAPLAMA - 4 ONDALIK (hassasiyet)
  // ═══════════════════════════════════════════════════════
  if (priceType === 'VAT_INCLUDED') {
    // KDV Dahil girildi
    unitPriceWithVat = roundTo4(enteredPrice);
    // ✅ 4 ONDALIK: 50 / 1.20 = 41.6667 TL (hassasiyet korunur!)
    unitPrice = roundTo4(unitPriceWithVat / (1 + vatRate / 100));
  } else {
    // KDV Hariç girildi (varsayılan)
    unitPrice = roundTo4(enteredPrice);
    unitPriceWithVat = roundTo4(unitPrice * (1 + vatRate / 100));
  }

  // ═══════════════════════════════════════════════════════
  // ADIM 2: SATIR TUTARLARI + İNDİRİM - 2 ONDALIK (faturalanabilir)
  // ═══════════════════════════════════════════════════════
  //
  // İNDİRİM TABANI (backend InvoiceManager.CalculateTotals ile BİREBİR aynı):
  //   • Kalem "KDV Dahil" (VAT_INCLUDED) ise indirim KDV DAHİL satır tutarına uygulanır,
  //     dolayısıyla discountAmount KDV DAHİL bir tutardır.
  //   • Kalem "KDV Hariç" (VAT_EXCLUDED) ise indirim KDV HARİÇ satır tutarına uygulanır,
  //     dolayısıyla discountAmount KDV HARİÇ bir tutardır.
  // İndirim modu (discountType):
  //   • AMOUNT → kullanıcı indirim TUTARI girer (kalemin KDV tabanında), oran türetilir.
  //   • PERCENT → kullanıcı ORAN girer, tutar hesaplanır.
  const discountType = item.discountType || 'PERCENT';
  let lineTotal: number;       // KDV HARİÇ, indirimsiz satır tutarı (özet "Ara Toplam")
  let discountAmount: number;  // İndirim tutarı (kalemin KDV tabanında)
  let effectiveDiscountRate: number;
  let netTotal: number;        // KDV HARİÇ, indirim sonrası
  let sctAmount: number;
  let vatAmount: number;
  let grossTotal: number;      // KDV DAHİL, indirim sonrası (özet "Genel Toplam")

  if (priceType === 'VAT_INCLUDED') {
    // KDV DAHİL fiyat → indirim KDV DAHİL tutara uygulanır
    const totalGross = roundTo2(enteredPrice * qty); // KDV dahil, indirimsiz
    if (discountType === 'AMOUNT') {
      const entered = Math.max(0, Math.min(item.discountAmount || 0, totalGross));
      discountAmount = roundTo2(entered);
      effectiveDiscountRate = totalGross > 0 ? roundTo2((discountAmount / totalGross) * 100) : 0;
    } else {
      discountAmount = roundTo2(totalGross * (discountRate / 100));
      effectiveDiscountRate = discountRate;
    }
    const discountedGross = roundTo2(totalGross - discountAmount);
    vatAmount = roundTo2(discountedGross - (discountedGross / (1 + vatRate / 100)));
    netTotal = roundTo2(discountedGross - vatAmount);
    sctAmount = roundTo2(netTotal * (sctRate / 100));
    lineTotal = roundTo2(totalGross / (1 + vatRate / 100)); // KDV hariç, indirimsiz
    grossTotal = discountedGross;
  } else {
    // KDV HARİÇ fiyat → indirim KDV HARİÇ tutara uygulanır
    lineTotal = roundTo2(unitPrice * qty);
    if (discountType === 'AMOUNT') {
      const entered = Math.max(0, Math.min(item.discountAmount || 0, lineTotal));
      discountAmount = roundTo2(entered);
      effectiveDiscountRate = lineTotal > 0 ? roundTo2((discountAmount / lineTotal) * 100) : 0;
    } else {
      discountAmount = roundTo2(lineTotal * (discountRate / 100));
      effectiveDiscountRate = discountRate;
    }
    netTotal = roundTo2(lineTotal - discountAmount);
    sctAmount = roundTo2(netTotal * (sctRate / 100));
    const vatBase = roundTo2(netTotal + sctAmount);
    vatAmount = roundTo2(vatBase * (vatRate / 100));
    grossTotal = roundTo2(vatBase + vatAmount);
  }

  return {
    ...item,
    unitPrice: roundTo2(unitPrice), // ✅ Görüntüleme için 2 ondalık
    unitPriceWithVat: roundTo2(unitPriceWithVat), // ✅ Görüntüleme için 2 ondalık
    lineTotal,
    discountRate: effectiveDiscountRate, // AMOUNT modunda tutardan türetilen oran
    discountAmount,
    netTotal,
    sctAmount,
    vatAmount,
    grossTotal,
  };
};

/**
 * Tüm fatura kalemlerini hesaplar ve toplamları döndürür
 * ✅ Toplamlar da 2 ondalık yuvarlama ile (kuruş hassasiyeti)
 */
export const calculateTotals = (items: InvoiceItem[]) => {
  let subtotal = 0;
  let netSubtotal = 0;
  let vatTotal = 0;
  let sctTotal = 0;
  let discountTotal = 0;
  let grandTotal = 0;
  const vatRateTotals: Record<number, number> = {};

  items.forEach((item) => {
    subtotal += item.lineTotal;
    netSubtotal += item.netTotal;
    vatTotal += item.vatAmount;
    sctTotal += item.sctAmount;
    discountTotal += item.discountAmount;
    grandTotal += item.grossTotal;

    // KDV oranına göre grupla
    if (!vatRateTotals[item.vatRate]) {
      vatRateTotals[item.vatRate] = 0;
    }
    vatRateTotals[item.vatRate] += item.vatAmount;
  });

  // ✅ Tüm toplamları 2 ondalık yuvarlama ile döndür
  return {
    subtotal: roundTo2(subtotal),
    netSubtotal: roundTo2(netSubtotal),
    // KDV hariç matrah indirimi (özet tutarlılığı için): Ara Toplam − İndirim Sonrası Ara Toplam.
    // discountAmount kalem KDV tabanında (dahil/hariç) olabildiği için özet bunu kullanır.
    discountNetTotal: roundTo2(subtotal - netSubtotal),
    vatTotal: roundTo2(vatTotal),
    sctTotal: roundTo2(sctTotal),
    discountTotal: roundTo2(discountTotal),
    grandTotal: roundTo2(grandTotal),
    vatRateTotals: Object.fromEntries(
      Object.entries(vatRateTotals).map(([rate, amount]) => [rate, roundTo2(amount)])
    ),
  };
};

/**
 * Tevkifat tutarını hesaplar
 * ✅ 2 ondalık yuvarlama ile
 */
export const calculateTevkifat = (vatTotal: number, tevkifatRate: string): number => {
  if (!tevkifatRate || tevkifatRate === '0') return 0;

  // Tevkifat oranını parse et (örn: "9/10" -> 0.9)
  const parts = tevkifatRate.split('/');
  if (parts.length === 2) {
    const numerator = parseFloat(parts[0]);
    const denominator = parseFloat(parts[1]);
    if (denominator !== 0) {
      return roundTo2(vatTotal * (numerator / denominator));
    }
  }

  // Yüzde olarak girilmiş olabilir (örn: "90")
  const percentage = parseFloat(tevkifatRate);
  if (!isNaN(percentage)) {
    return roundTo2(vatTotal * (percentage / 100));
  }

  return 0;
};

/**
 * Stopaj tutarını hesaplar
 * ✅ 2 ondalık yuvarlama ile
 */
export const calculateStopaj = (baseAmount: number, stopajRate: number): number => {
  if (!stopajRate || stopajRate === 0) return 0;
  return roundTo2(baseAmount * (stopajRate / 100));
};

/**
 * Para birimi formatı
 */
export const formatCurrency = (amount: number, currencyCode: string = 'TRY'): string => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Para birimi sembolünü döndürür
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: Record<string, string> = {
    TRY: '₺',
    USD: '$',
    EUR: '€',
    GBP: '£',
  };
  return symbols[currencyCode] || currencyCode;
};

/**
 * Fatura farkını hesaplar (yuvarlama farkları için)
 * ✅ 2 ondalık yuvarlama ile
 */
export const calculateInvoiceDifference = (
  calculatedTotal: number,
  originalTotal: number
): number => {
  if (!originalTotal || isNaN(originalTotal)) return 0;
  return roundTo2(originalTotal - calculatedTotal);
};

/**
 * Döviz kuru ile çarpım (TL karşılığı)
 * ✅ 2 ondalık yuvarlama ile
 */
export const convertToTRY = (amount: number, exchangeRate: string): number => {
  const rate = parseFloat(exchangeRate);
  if (isNaN(rate) || rate === 0) return amount;
  return roundTo2(amount * rate);
};

